import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
    ArrowDown,
    ArrowUp,
    ArrowUpRight,
    ChevronsUpDown,
    Copy,
    LayoutGrid,
    Monitor,
    MonitorSmartphone,
    MoreHorizontal,
    Pencil,
    Plus,
    Rows3,
    Search,
    Smartphone,
    Trash2,
    X,
} from "lucide-react";
import { Resource, TemplateDto, isTemplateSceneV2 } from "@exibra/shared";
import {
    useCustomTemplates,
    useDeleteTemplate,
    useDuplicateTemplate,
    useMigrateTemplateToV2,
} from "@/api/template.api";
import { useTenantStore } from "@/store/tenant/tenant.store";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { PaginationControls } from "@/components/pagination-controls/pagination-controls";
import { RenderWithPermissions } from "@/components/render-with-permissions/render-with-permissions";
import { V2ScenePreview } from "@/components/presentation/v2-scene-preview";
import { cn } from "@/lib/utils";
import { formatRelativeTime } from "@/lib/datetime";
import { TemplatePreview } from "./components/template-preview";
import { TemplatesStats } from "./components/templates-stats";
import { TemplateThumb } from "./components/template-thumb";
import { TemplateOrientation, getTemplateOrientation, getTemplateVersion } from "./template-meta";

type VersionFilter = "all" | "v2" | "v1";
type OrientationFilter = "all" | TemplateOrientation;
type ViewMode = "list" | "gallery";
type SortKey = "name" | "updatedAt";
type Sort = { key: SortKey; dir: "asc" | "desc" };

const VERSION_FILTERS: VersionFilter[] = ["all", "v2", "v1"];
const ORIENTATION_FILTERS: OrientationFilter[] = ["all", "horizontal", "vertical", "responsive"];
const PAGE_SIZE = 25;
const DEFAULT_SORT: Sort = { key: "updatedAt", dir: "desc" };

const ORIENTATION_ICON: Record<TemplateOrientation, typeof Monitor> = {
    horizontal: Monitor,
    vertical: Smartphone,
    responsive: MonitorSmartphone,
};

function matchesVersion(template: TemplateDto, filter: VersionFilter): boolean {
    if (filter === "all") return true;
    return `v${getTemplateVersion(template)}` === filter;
}

export const Templates = () => {
    const selectedTenant = useTenantStore((state) => state.selectedTenant);
    const navigate = useNavigate();
    const { t } = useTranslation();

    const { data: customTemplates, isLoading } = useCustomTemplates(selectedTenant?.id);
    const { mutate: deleteTemplate } = useDeleteTemplate();
    const { mutateAsync: duplicateTemplate } = useDuplicateTemplate();
    const { mutateAsync: migrateTemplate } = useMigrateTemplateToV2();

    const [search, setSearch] = useState("");
    const [version, setVersion] = useState<VersionFilter>("all");
    const [orientation, setOrientation] = useState<OrientationFilter>("all");
    const [sort, setSort] = useState<Sort>(DEFAULT_SORT);
    const [view, setView] = useState<ViewMode>("list");
    const [selected, setSelected] = useState<Set<number>>(new Set());
    const [page, setPage] = useState(1);

    const all = useMemo(() => customTemplates ?? [], [customTemplates]);

    // Global stats over the whole library (independent of the active filters).
    const stats = useMemo(
        () => ({
            total: all.length,
            v2: all.filter((tmpl) => getTemplateVersion(tmpl) === 2).length,
            v1: all.filter((tmpl) => getTemplateVersion(tmpl) === 1).length,
            responsive: all.filter((tmpl) => getTemplateOrientation(tmpl) === "responsive").length,
        }),
        [all],
    );

    // Apply search + orientation first so the version chip counts reflect the current context.
    const searched = useMemo(() => {
        const term = search.trim().toLowerCase();
        return all.filter((tmpl) => {
            if (term && !tmpl.name.toLowerCase().includes(term)) return false;
            if (orientation !== "all" && getTemplateOrientation(tmpl) !== orientation) return false;
            return true;
        });
    }, [all, search, orientation]);

    const versionCounts = useMemo(
        () => ({
            all: searched.length,
            v2: searched.filter((tmpl) => getTemplateVersion(tmpl) === 2).length,
            v1: searched.filter((tmpl) => getTemplateVersion(tmpl) === 1).length,
        }),
        [searched],
    );

    const filtered = useMemo(() => {
        const next = searched.filter((tmpl) => matchesVersion(tmpl, version));
        const dir = sort.dir === "asc" ? 1 : -1;
        return [...next].sort((a, b) => {
            if (sort.key === "name") return a.name.localeCompare(b.name) * dir;
            const at = new Date(a.updatedAt).getTime();
            const bt = new Date(b.updatedAt).getTime();
            return (at - bt) * dir;
        });
    }, [searched, version, sort]);

    const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
    const paged = useMemo(
        () => filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
        [filtered, page],
    );

    // Reset to the first page whenever the result set changes shape.
    useEffect(() => {
        setPage(1);
    }, [search, version, orientation, sort]);

    // Keep the page in range when the list shrinks (e.g. after a delete on a later page).
    useEffect(() => {
        if (page > totalPages && totalPages > 0) setPage(totalPages);
    }, [page, totalPages]);

    const allPageSelected = paged.length > 0 && paged.every((tmpl) => selected.has(tmpl.id));

    const toggleAll = useCallback(() => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (paged.length > 0 && paged.every((tmpl) => next.has(tmpl.id))) {
                paged.forEach((tmpl) => next.delete(tmpl.id));
            } else {
                paged.forEach((tmpl) => next.add(tmpl.id));
            }
            return next;
        });
    }, [paged]);

    const toggleOne = useCallback((id: number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleSort = useCallback((key: SortKey) => {
        setSort((prev) => {
            if (prev.key !== key) return { key, dir: key === "name" ? "asc" : "desc" };
            return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
        });
    }, []);

    const handleEdit = useCallback(
        (template: TemplateDto) => navigate(`edit/${template.id}`),
        [navigate],
    );

    const handleDuplicate = useCallback(
        async (template: TemplateDto) => {
            const tenantId = selectedTenant?.id ?? template.tenantId;
            try {
                const duplicated = await duplicateTemplate({ templateId: template.id, tenantId });
                navigate(`edit/${duplicated.id}`);
            } catch {
                // Error toast is surfaced by useDuplicateTemplate's onError handler.
            }
        },
        [duplicateTemplate, navigate, selectedTenant],
    );

    const handleMigrate = useCallback(
        async (template: TemplateDto) => {
            const tenantId = selectedTenant?.id ?? template.tenantId;
            try {
                const migrated = await migrateTemplate({ templateId: template.id, tenantId });
                navigate(`edit/${migrated.id}`);
            } catch {
                // Error toast is surfaced by useMigrateTemplateToV2's onError handler.
            }
        },
        [migrateTemplate, navigate, selectedTenant],
    );

    const handleDelete = useCallback(
        (template: TemplateDto) => {
            deleteTemplate({
                templateId: template.id,
                tenantId: selectedTenant?.id ?? template.tenantId,
            });
            setSelected((prev) => {
                if (!prev.has(template.id)) return prev;
                const next = new Set(prev);
                next.delete(template.id);
                return next;
            });
        },
        [deleteTemplate, selectedTenant],
    );

    const handleBulkDelete = useCallback(() => {
        const byId = new Map(all.map((tmpl) => [tmpl.id, tmpl]));
        selected.forEach((id) => {
            const tmpl = byId.get(id);
            if (!tmpl) return;
            deleteTemplate({ templateId: id, tenantId: selectedTenant?.id ?? tmpl.tenantId });
        });
        setSelected(new Set());
    }, [all, deleteTemplate, selected, selectedTenant]);

    return (
        <>
            <title>{t("templates.title")}</title>
            <div className="flex flex-col gap-3.5 pb-10">
                {/* Header */}
                <div className="flex flex-wrap items-start gap-4">
                    <div className="space-y-1">
                        <h1>{t("templates.title")}</h1>
                        <p className="text-sm text-muted-foreground">{t("templates.subtitle")}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-2">
                        <RenderWithPermissions module={Resource.Template} permissionLevel="create">
                            <Button size="sm" onClick={() => navigate("new")}>
                                <Plus />
                                {t("templates.actions.create")}
                            </Button>
                        </RenderWithPermissions>
                    </div>
                </div>

                <TemplatesStats
                    total={stats.total}
                    v2={stats.v2}
                    v1={stats.v1}
                    responsive={stats.responsive}
                />

                {/* Toolbar */}
                <div className="flex flex-wrap items-center gap-3 border-b pb-2.5">
                    <div className="flex gap-0.5 rounded-lg bg-muted p-0.5">
                        {VERSION_FILTERS.map((key) => (
                            <button
                                key={key}
                                onClick={() => setVersion(key)}
                                className={cn(
                                    "flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium text-muted-foreground transition-colors",
                                    version === key
                                        ? "bg-card text-foreground shadow-sm"
                                        : "hover:text-foreground",
                                )}
                            >
                                {t(`templates.tabs.${key}`)}
                                <span className="rounded bg-background/60 px-1.5 font-mono text-[10.5px] text-muted-foreground">
                                    {versionCounts[key]}
                                </span>
                            </button>
                        ))}
                    </div>

                    <Select
                        value={orientation}
                        onValueChange={(value) => setOrientation(value as OrientationFilter)}
                    >
                        <SelectTrigger className="h-8 w-[150px] text-xs">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {ORIENTATION_FILTERS.map((key) => (
                                <SelectItem key={key} value={key} className="text-xs">
                                    {t(`templates.orientationFilter.${key}`)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <div className="relative ml-auto w-full sm:w-56">
                        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("templates.searchPlaceholder")}
                            className="h-8 pl-8 text-xs"
                        />
                    </div>

                    <div className="flex gap-0.5 rounded-lg bg-muted p-0.5">
                        <ViewToggleButton
                            active={view === "list"}
                            onClick={() => setView("list")}
                            label={t("templates.view.list")}
                        >
                            <Rows3 className="size-3.5" />
                        </ViewToggleButton>
                        <ViewToggleButton
                            active={view === "gallery"}
                            onClick={() => setView("gallery")}
                            label={t("templates.view.gallery")}
                        >
                            <LayoutGrid className="size-3.5" />
                        </ViewToggleButton>
                    </div>
                </div>

                {view === "list" ? (
                    <TemplateTable
                        rows={paged}
                        isLoading={isLoading}
                        selected={selected}
                        allPageSelected={allPageSelected}
                        sort={sort}
                        onToggleAll={toggleAll}
                        onToggleOne={toggleOne}
                        onToggleSort={toggleSort}
                        onRowClick={handleEdit}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onMigrate={handleMigrate}
                        onDelete={handleDelete}
                    />
                ) : isLoading ? (
                    <EmptyState message={t("general.loading")} />
                ) : filtered.length === 0 ? (
                    <EmptyState message={t("general.noResults")} />
                ) : (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                        {paged.map((template) => (
                            <TemplateCard
                                key={template.id}
                                template={template}
                                selected={selected.has(template.id)}
                                onToggle={toggleOne}
                                onOpen={handleEdit}
                                onDuplicate={handleDuplicate}
                                onMigrate={handleMigrate}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}

                {totalPages > 1 && (
                    <div className="flex justify-center pt-1">
                        <PaginationControls
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </div>
                )}

                {/* Bulk action bar */}
                {selected.size > 0 && (
                    <div className="sticky bottom-4 z-10 mx-auto flex w-fit items-center gap-1 rounded-lg bg-brand-ink py-1 pl-3.5 pr-1 text-brand-paper shadow-lg">
                        <span className="mr-1.5 text-xs font-medium">
                            {t("templates.bulk.selected", { count: selected.size })}
                        </span>
                        <span className="mx-1 h-3.5 w-px bg-white/15" />
                        <RenderWithPermissions module={Resource.Template} permissionLevel="delete">
                            <button
                                onClick={handleBulkDelete}
                                className="flex h-6 items-center gap-1.5 rounded-md px-2 text-[11.5px] hover:bg-white/10"
                            >
                                <Trash2 className="size-3" />
                                {t("templates.bulk.remove")}
                            </button>
                        </RenderWithPermissions>
                        <span className="mx-1 h-3.5 w-px bg-white/15" />
                        <button
                            onClick={() => setSelected(new Set())}
                            className="flex size-6 items-center justify-center rounded-md bg-brand-lime text-brand-ink hover:bg-brand-lime-2"
                            aria-label={t("general.cancel")}
                        >
                            <X className="size-3" />
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

function ViewToggleButton({
    active,
    onClick,
    label,
    children,
}: {
    active: boolean;
    onClick: () => void;
    label: string;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={label}
            title={label}
            className={cn(
                "flex items-center justify-center rounded-md px-2 py-1 text-muted-foreground transition-colors",
                active ? "bg-card text-foreground shadow-sm" : "hover:text-foreground",
            )}
        >
            {children}
        </button>
    );
}

function EmptyState({ message }: { message: string }) {
    return (
        <div className="flex h-24 items-center justify-center rounded-lg border text-sm text-muted-foreground">
            {message}
        </div>
    );
}

function OrientationCell({ orientation }: { orientation: TemplateOrientation }) {
    const { t } = useTranslation();
    const Icon = ORIENTATION_ICON[orientation];
    return (
        <span className="inline-flex items-center gap-1.5 text-muted-foreground">
            <Icon className="size-3.5" />
            {t(`templates.orientation.${orientation}`)}
        </span>
    );
}

function VersionBadge({ template }: { template: TemplateDto }) {
    const version = getTemplateVersion(template);
    return (
        <Badge variant={version === 2 ? "default" : "secondary"} className="font-mono">
            v{version}
        </Badge>
    );
}

interface RowActionsHandlers {
    onEdit: () => void;
    onDuplicate: () => void;
    onMigrate: () => void;
    onDelete: () => void;
}

function TemplateActionsMenu({
    template,
    onEdit,
    onDuplicate,
    onMigrate,
    onDelete,
}: { template: TemplateDto } & RowActionsHandlers) {
    const { t } = useTranslation();
    const isV1 = getTemplateVersion(template) === 1;

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="size-7"
                    aria-label={t("general.actions")}
                >
                    <MoreHorizontal />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onSelect={onEdit}>
                    <Pencil className="size-3.5" />
                    {t("general.edit")}
                </DropdownMenuItem>
                <RenderWithPermissions module={Resource.Template} permissionLevel="create">
                    <DropdownMenuItem onSelect={onDuplicate}>
                        <Copy className="size-3.5" />
                        {t("general.duplicate")}
                    </DropdownMenuItem>
                    {isV1 && (
                        <DropdownMenuItem onSelect={onMigrate}>
                            <ArrowUpRight className="size-3.5" />
                            {t("templates.actions.migrate")}
                        </DropdownMenuItem>
                    )}
                </RenderWithPermissions>
                <RenderWithPermissions module={Resource.Template} permissionLevel="delete">
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                        onSelect={onDelete}
                        className="text-destructive focus:text-destructive"
                    >
                        <Trash2 className="size-3.5" />
                        {t("general.delete")}
                    </DropdownMenuItem>
                </RenderWithPermissions>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

interface TemplateTableProps {
    rows: TemplateDto[];
    isLoading: boolean;
    selected: Set<number>;
    allPageSelected: boolean;
    sort: Sort;
    onToggleAll: () => void;
    onToggleOne: (id: number) => void;
    onToggleSort: (key: SortKey) => void;
    onRowClick: (template: TemplateDto) => void;
    onEdit: (template: TemplateDto) => void;
    onDuplicate: (template: TemplateDto) => void;
    onMigrate: (template: TemplateDto) => void;
    onDelete: (template: TemplateDto) => void;
}

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
    if (!active) return <ChevronsUpDown className="size-3 text-muted-foreground" />;
    return dir === "desc" ? <ArrowDown className="size-3" /> : <ArrowUp className="size-3" />;
}

function TemplateTable({
    rows,
    isLoading,
    selected,
    allPageSelected,
    sort,
    onToggleAll,
    onToggleOne,
    onToggleSort,
    onRowClick,
    onEdit,
    onDuplicate,
    onMigrate,
    onDelete,
}: TemplateTableProps) {
    const { t } = useTranslation();

    return (
        <div className="overflow-x-auto rounded-lg border">
            <table className="w-full min-w-[760px] table-fixed border-collapse text-[12.5px]">
                <colgroup>
                    <col className="w-10" />
                    <col />
                    <col className="w-24" />
                    <col className="w-44" />
                    <col className="w-40" />
                    <col className="w-12" />
                </colgroup>
                <thead>
                    <tr className="border-b">
                        <th className="px-3 py-2.5 text-center">
                            <Checkbox
                                checked={allPageSelected}
                                onCheckedChange={onToggleAll}
                                aria-label={t("general.selectAll")}
                            />
                        </th>
                        <th className="px-2.5 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                            <button
                                className="inline-flex items-center gap-1"
                                onClick={() => onToggleSort("name")}
                            >
                                {t("templates.columns.name")}
                                <SortIcon active={sort.key === "name"} dir={sort.dir} />
                            </button>
                        </th>
                        <th className="px-2.5 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t("templates.columns.version")}
                        </th>
                        <th className="px-2.5 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                            {t("templates.columns.orientation")}
                        </th>
                        <th className="px-2.5 py-2.5 text-left text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
                            <button
                                className="inline-flex items-center gap-1"
                                onClick={() => onToggleSort("updatedAt")}
                            >
                                {t("templates.columns.lastEdited")}
                                <SortIcon active={sort.key === "updatedAt"} dir={sort.dir} />
                            </button>
                        </th>
                        <th />
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <tr>
                            <td
                                colSpan={6}
                                className="h-24 text-center text-sm text-muted-foreground"
                            >
                                {t("general.loading")}
                            </td>
                        </tr>
                    ) : rows.length === 0 ? (
                        <tr>
                            <td
                                colSpan={6}
                                className="h-24 text-center text-sm text-muted-foreground"
                            >
                                {t("general.noResults")}
                            </td>
                        </tr>
                    ) : (
                        rows.map((template) => {
                            const isSel = selected.has(template.id);
                            return (
                                <tr
                                    key={template.id}
                                    onClick={() => onRowClick(template)}
                                    role="button"
                                    tabIndex={0}
                                    aria-label={template.name}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            onRowClick(template);
                                        }
                                    }}
                                    className={cn(
                                        "cursor-pointer border-b transition-colors last:border-b-0 hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring",
                                        isSel &&
                                            "bg-accent/[0.08] shadow-[inset_2px_0_0_var(--brand-lime-2)]",
                                    )}
                                >
                                    <td
                                        className="px-3 text-center"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <Checkbox
                                            checked={isSel}
                                            onCheckedChange={() => onToggleOne(template.id)}
                                            aria-label={t("general.selectRow")}
                                        />
                                    </td>
                                    <td className="h-[46px] px-2.5">
                                        <div className="flex min-w-0 items-center gap-2.5 font-medium">
                                            <TemplateThumb template={template} />
                                            <span className="truncate">{template.name}</span>
                                        </div>
                                    </td>
                                    <td className="px-2.5">
                                        <VersionBadge template={template} />
                                    </td>
                                    <td className="px-2.5">
                                        <OrientationCell
                                            orientation={getTemplateOrientation(template)}
                                        />
                                    </td>
                                    <td className="truncate px-2.5 text-[11.5px] text-muted-foreground">
                                        {formatRelativeTime(template.updatedAt)}
                                    </td>
                                    <td
                                        className="px-1 text-center"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <TemplateActionsMenu
                                            template={template}
                                            onEdit={() => onEdit(template)}
                                            onDuplicate={() => onDuplicate(template)}
                                            onMigrate={() => onMigrate(template)}
                                            onDelete={() => onDelete(template)}
                                        />
                                    </td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>
        </div>
    );
}

/**
 * Renders a still preview of a template inside the gallery card. v1 templates use the Konva
 * preview; v2 templates use the dimension-aware DOM preview in its cheap static mode so portrait
 * and responsive layouts are framed correctly rather than squashed into 16:9.
 */
function TemplateGalleryPreview({ template }: { template: TemplateDto }) {
    if (isTemplateSceneV2(template.scene)) {
        return (
            <V2ScenePreview
                template={template}
                scene={template.scene}
                placeholders={{}}
                staticPreview
                style={{ width: "100%", height: "100%" }}
            />
        );
    }
    return (
        <TemplatePreview
            template={template}
            placeholderData={{}}
            style={{ width: "100%", height: "100%" }}
        />
    );
}

interface TemplateCardProps {
    template: TemplateDto;
    selected: boolean;
    onToggle: (id: number) => void;
    onOpen: (template: TemplateDto) => void;
    onDuplicate: (template: TemplateDto) => void;
    onMigrate: (template: TemplateDto) => void;
    onDelete: (template: TemplateDto) => void;
}

/**
 * Gallery card: a framed live preview with a solid metadata footer. Memoized so that typing in the
 * search box (which re-renders the parent) doesn't reconcile every card's canvas preview.
 */
const TemplateCard = memo(function TemplateCard({
    template,
    selected,
    onToggle,
    onOpen,
    onDuplicate,
    onMigrate,
    onDelete,
}: TemplateCardProps) {
    const { t } = useTranslation();
    const orientation = getTemplateOrientation(template);
    const OrientationIcon = ORIENTATION_ICON[orientation];

    return (
        <Card
            onClick={() => onOpen(template)}
            className={cn(
                "group relative cursor-pointer overflow-hidden p-0 transition-shadow hover:shadow-md",
                selected && "ring-2 ring-brand-lime-2",
            )}
        >
            <div className="relative aspect-video overflow-hidden border-b border-border/60 bg-muted">
                <div className="absolute inset-0">
                    <TemplateGalleryPreview template={template} />
                </div>
                <span className="absolute left-2 top-2" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        checked={selected}
                        onCheckedChange={() => onToggle(template.id)}
                        aria-label={t("general.selectItem")}
                        className={cn(
                            "border-border/70 bg-background/90 shadow-sm",
                            !selected &&
                                "opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100",
                        )}
                    />
                </span>
            </div>
            <div className="flex items-center gap-2 px-2.5 py-2">
                <span className="min-w-0 flex-1 truncate font-heading text-[13px] font-semibold tracking-tight">
                    {template.name}
                </span>
                <VersionBadge template={template} />
                <OrientationIcon className="size-3.5 flex-none text-muted-foreground" />
                <span className="-mr-1" onClick={(e) => e.stopPropagation()}>
                    <TemplateActionsMenu
                        template={template}
                        onEdit={() => onOpen(template)}
                        onDuplicate={() => onDuplicate(template)}
                        onMigrate={() => onMigrate(template)}
                        onDelete={() => onDelete(template)}
                    />
                </span>
            </div>
        </Card>
    );
});
